import { EmptyState, SectionCard } from '@atlas/ui-components'
import { Chip, type ChipProps, Skeleton } from '@atlas/ui-primitives'
import { Link } from 'react-router-dom'

export interface MyWorkOrderRow {
	workOrderId: string
	orderCode: string
	title: string
	/** Quiet trailing context — the mockup shows `brand · market`. */
	meta: string
	statusLabel: string
	statusVariant: ChipProps['variant']
	/** Token class painting the status dot, e.g. `bg-ds-info`. */
	dotClass: string
}

/**
 * The home page's "My work orders" card — the reader's own queue as compact
 * rows: status dot, code, title, quiet meta, status pill (Atlas ToBe).
 */
export function MyWorkOrdersCard({ rows, isLoading }: { rows: MyWorkOrderRow[]; isLoading?: boolean }) {
	return (
		<SectionCard
			title="My work orders"
			action={
				<Link to="/work-orders" className="text-xs font-medium text-ds-text hover:text-ds-info">
					Open board →
				</Link>
			}
			className="p-0 px-6 py-5.5"
		>
			{isLoading ? (
				<div aria-busy="true" className="border-t border-ds-border pt-3">
					<Skeleton shape="text" className="w-2/3" />
					<span className="sr-only">Loading…</span>
				</div>
			) : rows.length === 0 ? (
				<EmptyState className="mt-3 border-t border-ds-border" title="Nothing assigned to you right now." />
			) : (
				rows.map((row) => (
					<Link
						key={row.workOrderId}
						to={`/work-orders/${row.workOrderId}`}
						className="flex w-full items-center gap-3 border-t border-ds-border py-3 hover:bg-ds-surface"
					>
						<span className={`h-1.5 w-1.5 shrink-0 rounded-ds-full ${row.dotClass}`} />
						<span className="font-mono text-ds-2xs text-ds-text-subtle">{row.orderCode}</span>
						<span className="min-w-0 flex-1 truncate text-ds-sm text-ds-text">{row.title}</span>
						{row.meta ? (
							<span className="hidden truncate text-ds-2xs text-ds-text-subtle xl:inline">{row.meta}</span>
						) : null}
						<Chip variant={row.statusVariant} size="sm">
							{row.statusLabel}
						</Chip>
					</Link>
				))
			)}
		</SectionCard>
	)
}
